<template>
  <div
    class="card-footer"
    :style="cssStyles"
  >
    <Reaction
      v-if="isShowReactions"
      :hide-count="hideReactionCount"
    />

    <BHLink
      v-if="isShowComments"
      class="card-footer__comments-link"
      :target="newTab ? '_blank' : null"
      :href="articleLink.href"
      :class="commentsClasses"
      :aria-label="`Перейти к коментариям статьи ${article.title}`"
      colorless
      @click="sendAnalyticsEventComments(articleLink.href)"
    >
      <SvgAsset
        class="card-footer__icon"
        :content="commentsSvg"
      />
      <span
        v-if="!hideCommentsCount"
        class="card-footer__comments-count"
      >
        {{ article.comments_count || '' }}
      </span>
    </BHLink>

    <div
      v-if="!hideShare"
      class="card-footer__share-container"
      @mouseenter="handleMouseEnter"
      @mouseleave="sharingHover = false"
    >
      <BaseButton
        class="card-footer__button card-footer__button--share"
        :aria-label="`Поделиться статьей ${article.title}`"
        @click.stop="toggleSharing"
      >
        <SvgAsset
          class="card-footer__icon"
          :content="shareSvg"
        />
      </BaseButton>

      <SharingDesktop
        class="card-footer__social-sharing"
        :class="sharingMenuClass"
        :article="article"
        :shown="sharingHover"
      />
    </div>

    <div
      v-if="isShowDate || isShowCategories"
      class="card-footer__categories"
    >
      <span v-if="isShowDate"
        ><time :datetime="dateMoscow">{{ date }}</time> /&nbsp;</span
      >
      <template v-for="(category, index) in articleCategories">
        <component
          :is="checkSlug(category.slug)"
          v-if="isShowCategories"
          :key="category.id"
          class="card-footer__categories--category"
          :target="newTab ? '_blank' : null"
          :href="category.url"
          tap-area-expansion="10px"
          v-bind="categoryBind(category)"
          colorless
          @click="sendCategoryAnalytics(category)"
        >
          {{ category.title }}
          <span
            v-if="index !== articleCategories.length - 1 && isShowCategories"
            :key="index"
            class="card-footer__categories--separator"
          >
            /&nbsp;
          </span>
        </component>
      </template>
    </div>

    <div
      v-if="isShowPromoCategories"
      class="card-footer__categories"
    >
      <span v-if="isPromo">Реклама</span>
      <span v-if="isAdvertising && !isPromo">{{ partnerText }}</span>
    </div>
  </div>
</template>

<script lang="ts">
  import type { PropType } from 'vue';
  import { vOnClickOutside } from '@vueuse/components';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import { getDateText } from '~/utils/date';
  import Reaction from '~/components/Reaction/TheReaction.vue';
  import type { IFullArticle, TArticle, TCategory } from '~/types/common';
  import SharingDesktop from '~/components/Sharing/SharingDesktop.vue';
  import SharingMobile from '~/components/Sharing/SharingMobile.vue';
  import BaseButton from '~/components/BaseComponents/BaseButton.vue';
  import BHLink from '~/components/BHLink.vue';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { ADS_LABELS } from '~/constants';
  import { isTouchDevice } from '~/utils';
  import { useModal } from '@devhacker/shared/plugins/modal/useModal';
  import { useServiceErrorsStore } from '~/store/errors';

  import commentsSvg from '@/assets/images/svg/comments.svg?raw';
  import shareSvg from '@/assets/images/svg/share.svg?raw';
  import { convertBackendTimestampToMoscowString } from '@devhacker/shared/utils/date';

  export default defineNuxtComponent({
    name: 'CardFooter',

    components: {
      SvgAsset,
      SharingDesktop,
      SharingMobile,
      BaseButton,
      BHLink,
      Reaction,
    },

    directives: {
      clickOutside: vOnClickOutside,
    },

    inject: {
      // Категория в которой происходит событие
      // Для аналитики
      eventCategory: {
        default() {
          return () => '';
        },
      },

      // Контекст события для аналитики
      // Пример: Переход к комментариям, коммнетариям и т.д.
      eventActionComments: {
        default() {
          return () => '';
        },
      },

      // Контекст события для аналитики
      // Где находится элемент с которым происходит событие
      eventItemSharingList: {
        default: '',
      },

      // Контекст события для аналитики
      // Тоже самое что и в eventCategory?
      eventActionCategory: {
        default() {
          return () => '';
        },
      },

      // Контекст события для аналитики
      // Где находится кнопка комментариев с которым происходит событие
      eventItemComments: {
        default() {
          return () => '';
        },
      },
    },

    props: {
      article: {
        type: Object as PropType<TArticle | IFullArticle>,
        required: true,
      },

      hideComments: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      hideCommentsCount: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      hideReactions: {
        type: Boolean as PropType<boolean>,
        default: true,
      },

      hideReactionCount: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      hideShare: {
        type: Boolean as PropType<boolean>,
        default: true,
      },

      hideDate: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      hideCategories: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      isShowCommentsAsButton: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      isPlug: {
        type: Boolean as PropType<boolean>,
        default: false,
      },

      newTab: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      sharingOpen: false,
      sharingHover: false,
    }),

    computed: {
      commentsSvg: () => commentsSvg,
      shareSvg: () => shareSvg,

      isReactionServiceError() {
        return useServiceErrorsStore().reactionsServiceError;
      },

      isCommentsServiceError() {
        return useServiceErrorsStore().commentsServiceError;
      },

      partnerText(): string {
        const signText = this.article?.advert_label;
        return ADS_LABELS[signText] || '';
      },

      sharingMenuClass(): Record<string, boolean> {
        return {
          'card-footer__social-sharing--shown': this.sharingHover,
        };
      },

      cssStyles(): Record<string, string> {
        return {
          '--description-color': this.isPlug ? '#000' : '#4c4c4c',
        };
      },

      commentsClasses(): Record<string, boolean> {
        return {
          'card-footer__button': this.isShowCommentsAsButton,
        };
      },

      isCommentsEnabled(): boolean {
        return this.article.comments_enabled;
      },

      isPromo(): boolean {
        return this.article.is_promo;
      },

      isAdvertising(): boolean {
        return this.article.is_advertising;
      },

      articleCategories(): Array<TCategory> {
        return this.article.categories.slice(0, 2);
      },

      date(): string {
        return this.getDateText(this.article.date);
      },

      dateMoscow(): string {
        return convertBackendTimestampToMoscowString(this.article.date);
      },

      articleLink(): HTMLAnchorElement {
        return {
          // href: this.article.url,
          href: `${this.article.url}#comments`,
        } as HTMLAnchorElement;
      },

      currentSlug(): string {
        return getSlugFromUrl(useRoute().fullPath);
      },

      isShowReactions(): boolean {
        if (this.isReactionServiceError) {
          return false;
        }

        return (
          !this.hideReactions &&
          !this.isPromo &&
          !this.isAdvertising &&
          this.article?.reactions_enabled
        );
      },

      isShowComments(): boolean {
        if (this.isCommentsServiceError) {
          return false;
        }

        return !this.hideComments && this.isCommentsEnabled && !this.isPromo;
      },

      isShowDate(): boolean {
        return !this.hideDate && !(this.isPromo || this.isAdvertising);
      },

      isShowCategories(): boolean {
        return !this.hideCategories && !(this.isPromo || this.isAdvertising);
      },

      isShowPromoCategories(): boolean {
        return !this.hideCategories && (this.isPromo || this.isAdvertising);
      },
    },

    methods: {
      // Функция для аналитики клика перехода к комментариям
      sendAnalyticsEventComments(link: string) {
        this.$sendYandexMetrika({
          level1: 'Комментарии_переход к комментариям',
          level3: link,
          // @ts-expect-error injected analytics helpers are not typed in options API
          level6: this.eventItemComments(),
        });
      },

      // Функция для аналитики выбора рубрики
      sendCategoryAnalytics(_category: TCategory) {
        // const nextPageSlug = getSlugFromUrl(category.url);
      },

      getDateText,

      onClickCommentIcon(event: Event): void {
        event.preventDefault();
        const comments: HTMLDivElement | null = document.querySelector('#comments');
        if (comments) {
          comments.scrollIntoView();
        }
      },

      checkSlug(slug: string) {
        const slugRoute = useRoute().params.categorySlug;
        return slugRoute === slug ? 'span' : 'BHLink';
      },

      categoryBind(category: TCategory) {
        const slugRoute = useRoute().params.categorySlug;
        return slugRoute === category.slug ? null : { href: category.url };
      },

      handleMouseEnter() {
        this.sharingHover = !isTouchDevice();
      },

      toggleSharing() {
        if (!isTouchDevice()) return;

        if (navigator.share !== undefined) {
          this.sendWebShareEvent('Открытие меню');
          navigator
            .share({
              url: this.article.url,
              title: this.article.title,
            })
            .then(() => this.sendWebShareEvent('Переход в приложение'))
            .catch(() => this.sendWebShareEvent('Закрытие меню'));
        } else {
          useModal().open(
            SharingMobile,
            {
              closeClickOutside: true,
              article: this.article,
            },
            {
              transition: 'modal-fade',
            },
          );
        }
      },

      // Функция для аналитики "поделитьяс"
      sendWebShareEvent(_action: string) {},

      closeSharing() {
        if (this.sharingOpen) {
          this.sharingOpen = false;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  .card-footer {
    color: var(--description-color);
    font-family: $text-font--1;
    font-size: 14px;
    line-height: 20px;
    display: flex;
    align-items: center;

    &__comments {
      &-link {
        display: flex;
        align-items: center;
        position: relative;
        margin-right: $spacing-3;
        z-index: 3;

        // Fallback for browsers that don't support :has()
        :deep() .link-content {
          display: inline-flex;
        }
      }

      &-count {
        margin-left: 2px;
      }
    }

    &__button {
      @include smallText;
      font-weight: 500;
      padding: $spacing-1 $spacing-3;
      color: $text-color--1;
      border-radius: 40px;
      margin-right: $spacing-2;
      background: $bg-color--2;
      display: flex;
      align-items: center;

      @include hover {
        background: $hover-color--5;
      }
    }

    &__icon {
      display: flex;
      color: var(--description-color);
      width: 24px;
      height: 24px;
      margin-bottom: 2px;
    }

    &__share-container {
      position: relative;
      .card-footer__label {
        margin-left: 2px;
      }
    }

    &__social-sharing {
      position: absolute;
      visibility: hidden;
      transition: ease-in 0.3s;
      left: 0;
      bottom: 0;
      transform: translateY(calc(100% + 8px));
      z-index: 9999999999;
      opacity: 0;
      display: none;

      @include tablet {
        display: block;
      }

      &--shown {
        opacity: 1;
        visibility: visible;
        transition-delay: 0s;
        pointer-events: all;
      }
    }
    &__categories {
      display: flex;
      flex-wrap: wrap;
      color: var(--description-color);

      :deep() {
        .link-content {
          gap: 5px;
          display: flex;
        }
      }

      &--category {
        z-index: 3;
      }
    }
  }
</style>
